import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ClipboardCheck, ShieldAlert, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import type { SessionUser, UnclassifiedObject } from '@idas/shared';
import { api, ApiError } from '../../lib/api';
import { EmptyState, ListSkeleton } from '../../components/ui/EmptyState';
import { InlineSpinner, RefreshingOverlay } from '../../components/ui/Refreshing';
import { toast } from '../../components/ui/Toast';

/**
 * The compensating control for denylist mode.
 *
 * Running denylist means a table created in the warehouse tomorrow is readable
 * by every analyst the moment it appears — a restore, a vendor's schema, a
 * `payroll_2026_temp` someone made for an afternoon. Nobody is going to notice
 * that on their own.
 *
 * The catalog scanner flags every object no rule covers and surfaces it here,
 * highlighted when its column names look like they hold something sensitive.
 * This queue is the thing that has to actually get read.
 */
export function ReviewTab({ me }: { me: SessionUser }) {
  const queryClient = useQueryClient();
  const canWrite = me.permissions.includes('policy:write');
  const [dataSourceId, setDataSourceId] = useState<string>('');

  const datasources = useQuery({ queryKey: ['datasources','all'], queryFn: api.datasources.listAll });
  const objects = useQuery({
    queryKey: ['catalog', 'unclassified', dataSourceId],
    queryFn: () => api.catalog.unclassified(dataSourceId || undefined),
  });
  const mode = useQuery({ queryKey: ['policy', 'mode'], queryFn: api.policy.mode });

  const acknowledge = useMutation({
    mutationFn: (object: UnclassifiedObject) =>
      api.catalog.acknowledge({
        dataSourceId: object.dataSourceId,
        database: object.database,
        table: object.table,
      }),
    onSuccess: (_, object) => {
      toast.success(`${object.database}.${object.table} marked as reviewed.`);
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'unclassified'] });
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not save that.'),
  });

  // Sensitive-looking objects first: if the queue is long, these are the ones
  // that matter, and burying them under an alphabetical list defeats the point.
  const sorted = [...(objects.data?.objects ?? [])].sort(
    (a, b) => b.suspiciousColumns.length - a.suspiciousColumns.length,
  );
  const flagged = sorted.filter((o) => o.suspiciousColumns.length > 0).length;

  return (
    <div className="space-y-4">
      {mode.data?.mode === 'DENYLIST' && sorted.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-warn/30 bg-warn/5 p-3">
          <ShieldAlert className="mt-px h-4 w-4 shrink-0 text-warn" />
          <p className="text-2xs leading-relaxed text-ink">
            <span className="font-semibold">
              {sorted.length} object{sorted.length === 1 ? '' : 's'} nobody has classified
            </span>
            {flagged > 0 && (
              <span className="text-warn">
                {' '}
                — {flagged} with column names suggesting sensitive data
              </span>
            )}
            . In denylist mode these are readable by every analyst right now. Add a rule, or mark
            them reviewed to confirm that is intended.
          </p>
        </div>
      )}

      <section className="panel rounded-lg">
        <header className="flex flex-wrap items-center gap-2 border-b border-edge px-3 py-2">
          <ClipboardCheck className="h-3.5 w-3.5 text-ink-muted" />
          <h2 className="text-xs font-semibold text-ink">Unreviewed objects</h2>
          {/* Beside the title, where the eye already is when a filter changes
              or the queue reloads after something is marked reviewed. */}
          {objects.isFetching && !objects.isLoading && <InlineSpinner />}
          <div className="flex-1" />
          <select
            className="input w-auto py-1"
            value={dataSourceId}
            onChange={(event) => setDataSourceId(event.target.value)}
          >
            <option value="">All connections</option>
            {datasources.data?.datasources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
        </header>

        {objects.isLoading && <ListSkeleton rows={4} height={52} />}

        {!objects.isLoading && sorted.length === 0 && (
          <EmptyState
            icon={Sparkles}
            title="Nothing waiting"
            body="Every object in the catalog has been reviewed. New tables will appear here within an hour of being created, or immediately after a manual schema refresh."
          />
        )}

        <RefreshingOverlay active={objects.isFetching && !objects.isLoading}>
        <div className="divide-y divide-edge">
          {sorted.map((object) => {
            const suspicious = object.suspiciousColumns.length > 0;
            return (
              <div
                key={`${object.dataSourceId}.${object.database}.${object.table}`}
                className={clsx('px-3 py-2.5', suspicious && 'bg-warn/[0.03]')}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <code className="block truncate font-mono text-2xs font-medium text-ink">
                      {object.database}.{object.table}
                    </code>
                    <p className="text-2xs text-ink-faint">
                      first seen {new Date(object.firstSeenAt).toLocaleString()}
                      {object.approxRows !== null &&
                        ` · ~${object.approxRows.toLocaleString()} rows`}
                    </p>
                  </div>

                  {canWrite && (
                    <button
                      className="btn"
                      onClick={() => acknowledge.mutate(object)}
                      disabled={acknowledge.isPending}
                    >
                      <Check className="h-3 w-3" />
                      Mark reviewed
                    </button>
                  )}
                </div>

                {suspicious && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-md border border-warn/30 bg-warn/5 p-2">
                    <ShieldAlert className="h-3 w-3 shrink-0 text-warn" />
                    <span className="text-2xs text-warn">Column names to look at:</span>
                    {object.suspiciousColumns.map((column) => (
                      <code
                        key={column}
                        className="chip border-warn/30 bg-warn/10 font-mono text-warn"
                      >
                        {column}
                      </code>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </RefreshingOverlay>
      </section>

      <p className="px-1 text-2xs leading-relaxed text-ink-faint">
        This is a heuristic on column names, not a judgement about the data. It will miss things
        that matter and flag things that do not — it exists to make sure new objects get looked at,
        not to decide for you.
      </p>
    </div>
  );
}
